/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Owns the application runtime lifecycle:
 * session bootstrap, auth refresh on 401, dirty-form navigation guards,
 * URL token / masquerade bootstrap, and app-permission routing gates.
 */
import {Inject, Injectable} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import {ApiConfig} from '../../components/api/api';
import {AUTH_EVENT} from '../../components/auth/auth-constants';
import {AuthService} from '../../components/auth/auth.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UpdateSession} from '../../components/session/update-session';
import {AdnAuthHelperService} from '../../components/util/adn-auth-helper.service';
import {AdnFormStateService} from '../../components/util/adn-form-state.service';
import {AdnLogService} from '../../components/util/adn-log.service';
import {AdnApi404NavigationService} from '../errors/adn-api-404-navigation.service';
import {ConfirmNavigationModalService} from '../common/modal/confirm-navigation-modal.service';
import {AdnRollbarService, ROLLBAR_ENABLED_ON_LOCALHOST} from '../../components/util/adn-rollbar.service';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {LocalStorage} from '../../components/session/local-storage';
import {AuthHttpBufferService} from '../../components/auth/auth-http-buffer.service';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {AuthReturnNavigationService} from '../sign-in/auth-return-navigation.service';

export function deriveQueryParams(url: any): any {
  if (!_.isString(url) || url.length < 2 || url.indexOf('?') < 0) {
    return {};
  }
  const dataObj: any = {};
  const query = url.split('?')[1].split('#')[0];
  _.forEach(query.split('&'), function(str) {
    const equalsIndex = str.indexOf('=');
    if (equalsIndex < 1) {
      return;
    }
    const key = decodeURIComponent(str.substring(0, equalsIndex));
    const value = decodeURIComponent(str.substring(equalsIndex + 1));
    dataObj[key] = value;
  });
  return dataObj;
}

export function deriveTokens(url: any): any {
  const dataObj = deriveQueryParams(url);
  if (dataObj.accessToken && dataObj.username && dataObj.refreshToken && dataObj.expiresIn) {
    return dataObj;
  }
}

export function deriveUpdateSession(url: any): any {
  const dataObj = deriveQueryParams(url);
  if (dataObj.context && dataObj.auth_token) {
    return dataObj;
  }
}

export function isFatalRefreshError(rejection: any): boolean {
  const status = rejection && rejection.status;
  // Invalid/revoked credentials are terminal. Timeouts, rate limiting and
  // server/network failures should retain the refresh token for another try.
  return status === 400 || status === 401 || status === 403;
}

export function deriveRejectedAccessToken(rejection: any): string | undefined {
  const config = rejection && rejection.config;
  const queryToken = config && config.params && config.params.auth_token;
  if (queryToken) {
    return queryToken;
  }
  const authorization = config && config.headers && (config.headers.Authorization || config.headers.authorization);
  if (_.isString(authorization) && _.startsWith(authorization, 'Bearer ')) {
    return authorization.substring('Bearer '.length);
  }
}

export function isLocalHostname(hostname: any): boolean {
  const normalized = String(hostname || '').toLowerCase();
  return normalized === 'localhost'
    || normalized.endsWith('.localhost')
    || normalized === '127.0.0.1'
    || normalized === '::1'
    || normalized === '[::1]';
}

@Injectable({
  providedIn: 'root'
})
export class AppRuntimeLifecycleService {
  private initialized = false;
  private authCounter = 0;
  private lastUrl: string;
  private sessionBootstrapPromise: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly uiRouter: UIRouter,
    private readonly sessionAuth: AuthService,
    private readonly adnLog: AdnLogService,
    private readonly adnFormState: AdnFormStateService,
    private readonly adnAuthHelper: AdnAuthHelperService,
    private readonly adnApi404Navigation: AdnApi404NavigationService,
    private readonly confirmNavigationModalService: ConfirmNavigationModalService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly userPermissions: LocalUserPermissions,
    private readonly adnRollbar: AdnRollbarService,
    private readonly eventBus: AppEventBusService,
    private readonly stateService: StateService,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly authHttpBuffer: AuthHttpBufferService,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly authReturnNavigation: AuthReturnNavigationService
  ) {
  }

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const apiId = ApiConfig.obtain().getId();
    LocalStorage.obtain().setPrefix('adn' + (apiId ? '.' + apiId : ''));

    // Fallback URL when no state matches.
    this.uiRouter.urlService.rules.otherwise((_match, url) => {
      const path = (url && url.path) || this.location.pathname || '';
      if (this.sessionAuth.isAuthenticated() && !this.sessionAuth.getSession().isExpired()) {
        return path === '/' || !path ? '/home' : '/404';
      }
      if (path === '/sign-in/masquerade' || (typeof path === 'string' && path.indexOf('/sign-in/masquerade') === 0)) {
        return path;
      }
      return '/home';
    });

    const isLocalhost = isLocalHostname(this.location.hostname);
    const $transitions = this.uiRouter.transitionService;
    this.lastUrl = this.location.href;

    this.navigationHistory.reset();

    this.stateService.defaultErrorHandler((error: any) => {
      // 2 = SUPERSEDED, 3 = ABORTED, 5 = IGNORED — normal transition lifecycle, not app errors
      if (error && (error.type === 2 || error.type === 3 || error.type === 5)) {
        return;
      }
      const detail = error && error.detail;
      if (detail && detail.status === 404) {
        return;
      }
      this.adnLog.warn(error && error.toString ? error.toString() : error);
    });

    $transitions.onError({}, (trans: any) => {
      const rejection = trans.error();
      if (!rejection) {
        return;
      }
      const event = this.eventBus.emit(
        '$stateChangeError',
        trans.to(),
        trans.params('to'),
        trans.from(),
        trans.params('from'),
        rejection
      );
      if (rejection.type === 2 || rejection.type === 3 || rejection.type === 5) {
        return;
      }
      const response = this.adnApi404Navigation.findHttp404Response(rejection);
      if (response && !_.get(response, ['config', 'ignore404']) &&
          this.sessionAuth.isValidSession() && !this.sessionAuth.getSession().isExpired()) {
        return this.adnApi404Navigation.target(response);
      }
      if (event.defaultPrevented) {
        return;
      }
      if (!this.sessionAuth.isAuthenticated()) {
        return;
      }
      // INVALID — unknown state name or bad transition target
      if (rejection.type === 4) {
        return this.stateService.target('app.errors.404', null, {location: false, inherit: false});
      }
      // Resolve/other failure while leaving sign-in — don't leave the user on the login screen
      const fromState = trans.from();
      if (fromState && fromState.name === 'sign-in' && rejection.type === 6) {
        if (response) {
          return this.adnApi404Navigation.target(response);
        }
        return this.stateService.target('app.errors.404', null, {location: false, inherit: false});
      }
      this.adnLog.warn(rejection && rejection.toString ? rejection.toString() : rejection);
    });

    $transitions.onBefore({}, (transition: any) => {
      const toState = transition.to();
      const toParams = transition.params('to');
      const fromState = transition.from();
      const fromParams = transition.params('from');
      const event = this.eventBus.emit(
        '$stateChangeStart',
        toState,
        toParams,
        fromState,
        fromParams
      );
      this.rememberAuthReturnNavigation(toState, toParams);
      if (event.defaultPrevented) {
        return false;
      }

      if (!(toState && toState.data && toState.data.public) && !this.sessionAuth.isAuthenticated()) {
        this.eventBus.emit(AUTH_EVENT.notAuthenticated, toState, toParams);
        return false;
      }

      // Never block sign-out / public routes (sign-in) with the unsaved-changes dialog.
      if (toState && toState.data && toState.data.public) {
        return;
      }
      if (!this.sessionAuth.isAuthenticated() || !this.adnFormState.isDirty()) {
        return;
      }
      // Return a promise so UI-Router waits for the user's choice. Resolving true
      // continues the original transition; false aborts it (stay on page).
      return this.confirmNavigationModalService.confirm().then((keepGoing: boolean) => {
        if (keepGoing) {
          this.adnFormState.deregister();
          return true;
        }
        return false;
      });
    });

    $transitions.onSuccess({}, (transition: any) => {
      const toState = transition.to();
      const toParams = transition.params('to');
      const fromState = transition.from();
      const fromParams = transition.params('from');

      this.eventBus.emit('$stateChangeSuccess', toState, toParams, fromState, fromParams);
      this.handleSuccessfulTransition(toState, fromState, fromParams);
      this.broadcastLocationChange();
    });

    this.window.addEventListener('beforeunload', (e: any) => {
      if (this.sessionAuth.isAuthenticated() && this.adnFormState.isDirty() && !isLocalhost) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes.';
      }
    });
    this.window.addEventListener('pageshow', () => this.refreshExpiredSessionOnReturn());
    this.window.addEventListener('focus', () => this.refreshExpiredSessionOnReturn());
    this.window.addEventListener('online', () => this.refreshExpiredSessionOnReturn());
    this.window.document.addEventListener('visibilitychange', () => {
      if (this.window.document.visibilityState === 'visible') {
        this.refreshExpiredSessionOnReturn();
      }
    });

    if (ApiConfig.obtain().getEnv() === 'production') {
      this.window.pendo.initialize({
        visitor: {
          id: 'VISITOR-UNIQUE-ID'
        }
      });
    }

    this.adnRollbar.configure({
      enabled: ROLLBAR_ENABLED_ON_LOCALHOST || !isLocalhost
    });
    this.syncRollbarPerson();
    this.eventBus.on(AUTH_EVENT.loginSuccess, () => {
      this.syncRollbarPerson();
    });
    this.eventBus.on(AUTH_EVENT.networkChange, () => {
      this.syncRollbarPerson();
    });
    this.eventBus.on(AUTH_EVENT.logoutSuccess, () => {
      this.adnRollbar.clearPerson();
    });
    this.eventBus.on(AUTH_EVENT.notAuthenticated, () => {
      this.adnRollbar.clearPerson();
    });

    this.eventBus.on('$locationChangeStart', (event: any, current: any, previous: any) => {
      this.handleLocationChangeStart(event, current, previous);
    });
    this.eventBus.on('$locationChangeSuccess', () => {
      this.clearBootstrapQueryParams();
    });

    // Process SSO / masquerade tokens before the first URL synchronization.
    const initialLocationEvent = this.eventBus.emit(
      '$locationChangeStart',
      this.location.href,
      ''
    );
    if (!initialLocationEvent.defaultPrevented) {
      this.eventBus.emit('$locationChangeSuccess', this.location.href, '');
    }

    // attempt to refresh an expired session before the initial URL transition
    let sessionBootstrapPromise = Promise.resolve();
    if (this.sessionAuth.isAuthenticated() && this.sessionAuth.getSession().isExpired()) {
      this.authHttpBuffer.startupSessionRefreshInProgress = true;
      sessionBootstrapPromise = this.sessionAuth.refresh().catch((rejection: any) => {
        if (isFatalRefreshError(rejection)) {
          this.sessionAuth.signOut();
        }
      }).finally(() => {
        this.authHttpBuffer.startupSessionRefreshInProgress = false;
      });
    }
    this.sessionBootstrapPromise = sessionBootstrapPromise;

    sessionBootstrapPromise.finally(() => {
      setTimeout(() => {
        this.uiRouter.urlService.listen();
        this.uiRouter.urlService.sync();
      }, 0, false);
    });

    // catch events triggered by API 401 response errors
    this.eventBus.on('event:auth-loginRequired', (_event: any, rejection: any) => {
      this.sessionBootstrapPromise.then(() => {
        if (this.authHttpBuffer.networkSwitchInProgress) {
          return;
        }
        if (this.confirmSessionAfterStaleRejection(rejection)) {
          return;
        }
        if (this.authCounter > 10) {
          if (this.sessionAuth.isValidSession() && !this.sessionAuth.getSession().isExpired()) {
            this.authHttpBuffer.loginConfirmed(this.sessionAuth.getSessionData());
            return;
          }
          this.handleUnauthenticated(rejection.data);
          return;
        }
        setTimeout(() => {
          this.authCounter = 0;
        }, 15000);
        this.authHttpBuffer.authRefreshInProgress = true;
        // attempt a token refresh
        this.sessionAuth.refresh().then((session: any) => {
          this.authCounter++;
          this.authHttpBuffer.loginConfirmed(session);
        }, (refreshRejection: any) => {
          if (!isFatalRefreshError(refreshRejection)) {
            if (this.sessionAuth.isValidSession() && !this.sessionAuth.getSession().isExpired()) {
              this.authHttpBuffer.loginConfirmed(this.sessionAuth.getSessionData());
            } else {
              // Keep the refresh token after an offline/server failure. Reject
              // this batch of requests, and allow resume/online or the next API
              // request to try refreshing again.
              this.authHttpBuffer.loginCancelled(null, refreshRejection);
            }
            return;
          }
          if (this.sessionAuth.isAuthenticated()) {
            this.handleUnauthenticated(refreshRejection && refreshRejection.data);
          } else {
            this.authHttpBuffer.loginCancelled(null, refreshRejection);
          }
        }).finally(() => {
          this.authHttpBuffer.authRefreshInProgress = false;
        });
      });
    });

  }

  private handleLocationChangeStart(event: any, current: any, previous: any): void {
    const currentUrl = _.isString(current) ? current : '';
    const previousUrl = _.isString(previous) ? previous : '';
    let updateSession = deriveUpdateSession(currentUrl);
    if (!updateSession) {
      updateSession = deriveUpdateSession(previousUrl);
    }
    if (updateSession) {
      UpdateSession.obtain().init(updateSession.context, updateSession.auth_token);
    }

    if (currentUrl === previousUrl || currentUrl.indexOf('hardstop') > -1 || previousUrl.indexOf('hardstop') > -1) {
      return;
    }

    const tokens = deriveTokens(currentUrl) || deriveTokens(previousUrl);
    if (!tokens) {
      return;
    }

    this.sessionAuth.switchUi(
      tokens.username,
      tokens.masqueraderUsername,
      tokens.accessToken,
      tokens.refreshToken,
      tokens.expiresIn
    );
    event.preventDefault();

    const isMasquerading = tokens.username !== tokens.masqueraderUsername && tokens.masqueraderUsername;
    this.adnAuthHelper.doAuth({}, {
      username: tokens.username,
      masqUsername: isMasquerading ? tokens.masqueraderUsername : ''
    }, isMasquerading, tokens.context).then(() => {
      this.location.setQueryParam('hardstop', 'hardstop');
      setTimeout(() => {
        this.window.location.reload();
      }, 100);
    });
  }

  private clearBootstrapQueryParams(): void {
    const params = this.location.queryParams;
    [
      'username',
      'masqueraderUsername',
      'accessToken',
      'refreshToken',
      'expiresIn',
      'hardstop',
      'context',
      'auth_token'
    ].forEach((key) => {
      if (params[key] != null) {
        this.location.setQueryParam(key, null);
      }
    });
  }

  private broadcastLocationChange(): void {
    const currentUrl = this.location.href;
    const previousUrl = this.lastUrl || currentUrl;
    this.lastUrl = currentUrl;
    if (currentUrl === previousUrl) {
      return;
    }
    const event = this.eventBus.emit('$locationChangeStart', currentUrl, previousUrl);
    if (!event.defaultPrevented) {
      this.eventBus.emit('$locationChangeSuccess', currentUrl, previousUrl);
    }
  }

  private handleSuccessfulTransition(toState: any, fromState: any, fromParams: any): void {
    if (toState.name !== 'sign-in' && !_.startsWith(toState.name, 'sign-in.')) {
      this.clearAuthReturnNavigation();
    }
    const hideStatuspageEmbed = !!(toState && _.get(toState, 'data.public') && _.get(toState, 'data.preview'));
    this.window.document.body.classList.toggle('adn-hide-statuspage-embed', hideStatuspageEmbed);

    this.navigationHistory.remember(fromState, fromParams);

    const appsForPage = _.get(toState.data, 'app') || this.stateService.get(toState.parent) || [];
    const appsForPageArray = _.isArray(appsForPage) ? appsForPage : [appsForPage];
    const currentApp = this.localUserProfile.getCurrentApp();
    const allApps = this.userPermissions.getApps();

    if (appsForPageArray.length === 0) {
      return;
    }

    if (_.intersection(allApps, appsForPageArray).length === 0) {
      this.stateService.go('app.errors.404', null, {location: false, inherit: false});
    } else if (appsForPageArray.indexOf(currentApp) < 0) {
      const appsToGo = _.filter(appsForPageArray, (app) => app !== currentApp);
      this.stateService.go('app.errors.wrong-app', {appsToGo}, {location: false, inherit: false});
    }
  }

  private clearAuthReturnNavigation(): void {
    this.authReturnNavigation.clear();
  }

  private rememberAuthReturnNavigation(toState: any, toParams: any): void {
    this.authReturnNavigation.remember(toState, toParams);
  }

  private getAuthReturnNavigation(): any {
    const rememberedNavigation = this.authReturnNavigation.get();
    if (rememberedNavigation) {
      return rememberedNavigation;
    }
    if (this.stateService.current.name && !this.stateService.current.data?.public) {
      return {
        toState: this.stateService.current,
        toParams: _.cloneDeep(this.stateService.params)
      };
    }
    return null;
  }

  private syncRollbarPerson(): void {
    if (!this.sessionAuth.isAuthenticated() || !this.localUserProfile.get('userId')) {
      this.adnRollbar.clearPerson();
      return;
    }
    this.adnRollbar.syncPerson(this.localUserProfile, this.localNetworkProfile);
  }

  private confirmSessionAfterStaleRejection(rejection: any): boolean {
    if (!this.sessionAuth.isAuthenticated() || this.sessionAuth.getSession().isExpired()) {
      return false;
    }
    const rejectedAccessToken = deriveRejectedAccessToken(rejection);
    const currentSession = this.sessionAuth.getSessionData();
    if (!rejectedAccessToken || rejectedAccessToken === currentSession.accessToken) {
      return false;
    }
    this.authHttpBuffer.loginConfirmed(currentSession);
    return true;
  }

  private refreshExpiredSessionOnReturn(): void {
    if (this.authHttpBuffer.startupSessionRefreshInProgress
      || this.authHttpBuffer.authRefreshInProgress
      || this.authHttpBuffer.networkSwitchInProgress
      || !this.sessionAuth.isAuthenticated()
      || !this.sessionAuth.getSession().isExpired()) {
      return;
    }

    this.authHttpBuffer.authRefreshInProgress = true;
    this.sessionAuth.refresh().then((session: any) => {
      this.authHttpBuffer.loginConfirmed(session);
    }, (rejection: any) => {
      if (!isFatalRefreshError(rejection)) {
        this.authHttpBuffer.loginCancelled(null, rejection);
      } else if (this.sessionAuth.isAuthenticated()) {
        this.handleUnauthenticated(rejection && rejection.data);
      } else {
        this.authHttpBuffer.loginCancelled(null, rejection);
      }
    }).finally(() => {
      this.authHttpBuffer.authRefreshInProgress = false;
    });
  }

  private handleUnauthenticated(rejectionData: any): void {
    const returnNav = this.getAuthReturnNavigation();
    this.sessionAuth.signOut();
    this.authHttpBuffer.loginCancelled(null, rejectionData);
    setTimeout(() => {
      if (returnNav) {
        this.eventBus.emit(AUTH_EVENT.notAuthenticated, returnNav.toState, returnNav.toParams, rejectionData);
      } else {
        this.eventBus.emit(AUTH_EVENT.notAuthenticated, null, null, rejectionData);
      }
    });
  }
}
